import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ArrowLeft, Loader2, Users, Briefcase, ChevronRight, ChevronDown,
  Clock, CheckCircle2, Circle, ShieldCheck, Save, Pencil, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { FurnitureNavBar } from '@/components/furniture/FurnitureNavBar';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  adminHoursDetail, adminListPayPeriods, adminUpsertPayPeriod,
  adminUpdateHoursEntry, adminDeleteHoursEntry, fmtErr,
} from '@/lib/staffApi';
import { getFurnitureProjects } from '@/lib/api';

const PERM_LABEL = {
  full: 'Full project',
  commenter: 'View & comment',
  limited: 'Limited',
};

// ── Half-month pay period helpers ───────────────────────────────────
// Periods are [1–14] (closes on the 15th) and [15–end] (closes on the
// 1st of the following month). All dates here are YYYY-MM-DD strings to
// keep tz-shenanigans out of the math.
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
function lastDayOfMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
function periodFor(dateStr) {
  // dateStr is YYYY-MM-DD; parse as local date.
  const [y, m, d] = dateStr.split('-').map(Number);
  const month0 = m - 1;
  if (d < 15) {
    return { start: ymd(new Date(y, month0, 1)), end: ymd(new Date(y, month0, 14)) };
  }
  return { start: ymd(new Date(y, month0, 15)), end: ymd(new Date(y, month0, lastDayOfMonth(y, month0))) };
}
function nextPeriodAfter(p) {
  const [y, m, d] = p.start.split('-').map(Number);
  const month0 = m - 1;
  if (d === 1) {
    return { start: ymd(new Date(y, month0, 15)), end: ymd(new Date(y, month0, lastDayOfMonth(y, month0))) };
  }
  // d === 15 → next period starts on the 1st of next month
  const ny = month0 === 11 ? y + 1 : y;
  const nm = month0 === 11 ? 0 : month0 + 1;
  return { start: ymd(new Date(ny, nm, 1)), end: ymd(new Date(ny, nm, 14)) };
}
function periodLabel(p) {
  const fmtOne = (s) => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };
  const [y] = p.end.split('-');
  return `${fmtOne(p.start)} – ${fmtOne(p.end)}, ${y}`;
}
function isPeriodClosed(p, today = ymd(new Date())) {
  return p.end < today;
}

export default function StaffHoursPage() {
  const navigate = useNavigate();
  const adminPass = typeof window !== 'undefined' ? sessionStorage.getItem('app_password') || '' : '';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ users: [], projects: [], entries: [] });
  const [tab, setTab] = useState('staff'); // 'staff' | 'projects'
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  const load = useCallback(async () => {
    if (!adminPass) return;
    setLoading(true);
    try { setData(await adminHoursDetail(adminPass)); }
    catch (e) { toast.error(fmtErr(e, 'Failed to load hours')); }
    finally { setLoading(false); }
  }, [adminPass]);

  useEffect(() => { load(); }, [load]);

  const selectedUser = useMemo(
    () => data.users.find(u => u.user_id === selectedUserId) || null,
    [data.users, selectedUserId]
  );
  const selectedProject = useMemo(
    () => data.projects.find(p => p.project_id === selectedProjectId) || null,
    [data.projects, selectedProjectId]
  );

  return (
    <div className="min-h-screen bg-[#fbfaf7]">
      <FurnitureNavBar />
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate('/furniture-projects/staffing')} data-testid="hours-back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-2xl font-bold tracking-tight">Hours Log</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Pay periods, project breakdowns, and payment tracking.</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="inline-flex items-center bg-muted/40 rounded-lg p-1 mb-6">
          <TabButton active={tab === 'staff'} onClick={() => { setTab('staff'); setSelectedProjectId(null); }} testId="hours-tab-staff">
            <Users className="h-4 w-4" /> Staff <span className="opacity-60 text-xs">({data.users.length})</span>
          </TabButton>
          <TabButton active={tab === 'projects'} onClick={() => { setTab('projects'); setSelectedUserId(null); }} testId="hours-tab-projects">
            <Briefcase className="h-4 w-4" /> Projects <span className="opacity-60 text-xs">({data.projects.length})</span>
          </TabButton>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : tab === 'staff' ? (
          selectedUser
            ? <UserDetail user={selectedUser} entries={data.entries.filter(e => e.user_id === selectedUser.user_id)} adminPass={adminPass} onBack={() => setSelectedUserId(null)} onEntriesChanged={load} />
            : <StaffList users={data.users} onSelect={setSelectedUserId} />
        ) : (
          selectedProject
            ? <ProjectDetail project={selectedProject} entries={data.entries.filter(e => e.project_id === selectedProject.project_id)} onBack={() => setSelectedProjectId(null)} />
            : <ProjectsList projects={data.projects} onSelect={setSelectedProjectId} />
        )}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
        active ? 'bg-white shadow text-[#1f2933]' : 'text-muted-foreground hover:text-[#1f2933]'
      }`}
    >
      {children}
    </button>
  );
}

/* ── Staff list (profile cards) ─────────────────────────────────── */
function StaffList({ users, onSelect }) {
  if (users.length === 0) {
    return <p className="text-sm text-muted-foreground py-12 text-center">No staff or hours logged yet.</p>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="staff-cards-grid">
      {users.map(u => (
        <button
          key={u.user_id}
          onClick={() => onSelect(u.user_id)}
          className="group text-left rounded-xl border bg-white p-4 hover:shadow-md hover:border-emerald-300 transition-all"
          data-testid={`staff-card-${u.user_id}`}
        >
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-semibold flex-shrink-0">
              {(u.name || u.email || '?')[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate flex items-center gap-1.5">
                {u.name || '—'}
                {u.is_approver && <ShieldCheck className="h-3 w-3 text-emerald-600" title="Approver" />}
              </p>
              <p className="text-xs text-muted-foreground truncate">{u.email || '—'}</p>
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                {u.role && <Badge variant="outline" className="text-[10px] capitalize">{u.role}</Badge>}
                {u.permission_level && <Badge variant="outline" className="text-[10px]">{PERM_LABEL[u.permission_level] || u.permission_level}</Badge>}
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-emerald-600 transition-colors mt-1" />
          </div>
          <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Total hours</span>
            <span className="tabular-nums font-semibold text-[#1f2933]">{u.total_hours.toFixed(2)} hr</span>
          </div>
          <div className="flex items-center justify-between text-xs mt-1">
            <span className="text-muted-foreground">Sessions</span>
            <span className="tabular-nums text-muted-foreground">{u.entries}</span>
          </div>
          <div className="flex items-center justify-between text-xs mt-1">
            <span className="text-muted-foreground">Projects</span>
            <span className="tabular-nums text-muted-foreground">{u.by_project.length}</span>
          </div>
        </button>
      ))}
    </div>
  );
}

/* ── User detail with pay periods ───────────────────────────────── */
function UserDetail({ user, entries, adminPass, onBack, onEntriesChanged }) {
  const [periodMeta, setPeriodMeta] = useState({}); // keyed by period_start
  const [metaLoading, setMetaLoading] = useState(true);

  // Group entries into half-month buckets, newest first
  const periods = useMemo(() => {
    if (entries.length === 0) return [];
    const buckets = new Map(); // start -> {start, end, entries: []}
    entries.forEach(e => {
      if (!e.date) return;
      const p = periodFor(e.date);
      const key = p.start;
      if (!buckets.has(key)) buckets.set(key, { ...p, entries: [] });
      buckets.get(key).entries.push(e);
    });
    // Sort by start date desc
    return Array.from(buckets.values()).sort((a, b) => b.start.localeCompare(a.start));
  }, [entries]);

  // Refresh saved pay-period metadata for this user
  const refreshMeta = useCallback(async () => {
    setMetaLoading(true);
    try {
      const res = await adminListPayPeriods(adminPass, user.user_id);
      const map = {};
      (res.items || []).forEach(it => { map[it.period_start] = it; });
      setPeriodMeta(map);
    } catch (e) { toast.error(fmtErr(e, 'Failed to load pay periods')); }
    finally { setMetaLoading(false); }
  }, [adminPass, user.user_id]);

  useEffect(() => { refreshMeta(); }, [refreshMeta]);

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="text-sm text-muted-foreground hover:text-[#1f2933] inline-flex items-center gap-1" data-testid="user-detail-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to staff list
      </button>

      {/* Profile card */}
      <Card>
        <CardContent className="p-5 sm:p-6 flex flex-wrap items-start gap-5">
          <div className="h-16 w-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-2xl font-semibold flex-shrink-0">
            {(user.name || user.email || '?')[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-[200px]">
            <h2 className="font-heading text-xl font-semibold text-[#1f2933] flex items-center gap-2">
              {user.name || '—'}
              {user.is_approver && <ShieldCheck className="h-4 w-4 text-emerald-600" title="Approver" />}
            </h2>
            <p className="text-sm text-muted-foreground">{user.email || '—'}</p>
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {user.role && <Badge variant="outline" className="text-[10px] capitalize">{user.role}</Badge>}
              {user.permission_level && <Badge variant="outline" className="text-[10px]">{PERM_LABEL[user.permission_level] || user.permission_level}</Badge>}
              <Badge variant="outline" className="text-[10px]">{user.project_ids.length} project{user.project_ids.length === 1 ? '' : 's'} assigned</Badge>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-right">
            <StatBlock label="Total hours" value={`${user.total_hours.toFixed(2)} hr`} />
            <StatBlock label="Sessions" value={user.entries} />
          </div>
        </CardContent>
      </Card>

      {/* Project breakdown — across all time */}
      {user.by_project.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Briefcase className="h-4 w-4 text-primary" />Hours by project (all time)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {user.by_project.map(bp => (
                <div key={bp.project_id} className="flex items-center gap-3 text-sm" data-testid={`user-by-project-${bp.project_id}`}>
                  <span className="flex-1 truncate">{bp.project_name}</span>
                  <span className="text-xs text-muted-foreground tabular-nums">{bp.entries} session{bp.entries === 1 ? '' : 's'}</span>
                  <span className="w-20 text-right tabular-nums font-semibold">{bp.hours.toFixed(2)} hr</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pay periods */}
      <div className="space-y-3">
        <h3 className="font-heading text-lg font-semibold text-[#1f2933] flex items-center gap-2">
          <Clock className="h-4 w-4" /> Pay periods
          {metaLoading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
        </h3>
        {periods.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No hours logged yet.</p>
        ) : periods.map(p => (
          <PeriodCard
            key={p.start}
            period={p}
            meta={periodMeta[p.start]}
            adminPass={adminPass}
            userId={user.user_id}
            onSaved={refreshMeta}
            onEntriesChanged={onEntriesChanged}
          />
        ))}
      </div>
    </div>
  );
}

function StatBlock({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-base font-heading font-semibold tabular-nums text-[#1f2933]">{value}</p>
    </div>
  );
}

/* ── Single pay-period card ─────────────────────────────────────── */
function PeriodCard({ period, meta, adminPass, userId, onSaved, onEntriesChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);   // entry being edited
  const [deletingEntry, setDeletingEntry] = useState(null); // entry pending delete-confirm
  const totalHours = period.entries.reduce((s, e) => s + (e.hours || 0), 0);
  const closed = isPeriodClosed(period);
  const paid = !!meta?.paid;

  // Per-project breakdown within this period
  const byProject = useMemo(() => {
    const m = new Map();
    period.entries.forEach(e => {
      const cur = m.get(e.project_id) || { project_id: e.project_id, project_name: e.project_name, hours: 0, entries: 0 };
      cur.hours += e.hours || 0;
      cur.entries += 1;
      m.set(e.project_id, cur);
    });
    return Array.from(m.values()).sort((a, b) => b.hours - a.hours);
  }, [period.entries]);

  const statusBadge = paid ? (
    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100"><CheckCircle2 className="h-3 w-3 mr-1" />Paid</Badge>
  ) : closed ? (
    <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100"><Circle className="h-3 w-3 mr-1" />Closed · Unpaid</Badge>
  ) : (
    <Badge variant="outline">Open</Badge>
  );

  return (
    <Card data-testid={`period-card-${period.start}`}>
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors"
      >
        {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{periodLabel(period)}</p>
          <p className="text-xs text-muted-foreground">{period.entries.length} session{period.entries.length === 1 ? '' : 's'} · {byProject.length} project{byProject.length === 1 ? '' : 's'}</p>
        </div>
        {statusBadge}
        <span className="tabular-nums font-semibold text-sm w-20 text-right">{totalHours.toFixed(2)} hr</span>
      </button>

      {expanded && (
        <CardContent className="border-t bg-muted/10 p-4 sm:p-5 space-y-5">
          {/* By project */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">Hours by project</p>
            <div className="space-y-1.5">
              {byProject.map(bp => (
                <div key={bp.project_id} className="flex items-center gap-3 text-sm" data-testid={`period-by-project-${bp.project_id}`}>
                  <span className="flex-1 truncate">{bp.project_name}</span>
                  <span className="text-xs text-muted-foreground tabular-nums">{bp.entries}×</span>
                  <span className="w-20 text-right tabular-nums font-medium">{bp.hours.toFixed(2)} hr</span>
                </div>
              ))}
            </div>
          </div>

          {/* Entry list */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">All sessions</p>
            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-medium">Date</th>
                    <th className="text-left px-3 py-1.5 font-medium">Project</th>
                    <th className="text-left px-3 py-1.5 font-medium">Time</th>
                    <th className="text-right px-3 py-1.5 font-medium">Hours</th>
                    <th className="text-left px-3 py-1.5 font-medium">Notes</th>
                    <th className="text-right px-3 py-1.5 font-medium w-[80px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {period.entries
                    .slice()
                    .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
                    .map(e => (
                      <tr key={e.id} className="hover:bg-muted/20 group">
                        <td className="px-3 py-1.5 tabular-nums">{e.date || '—'}</td>
                        <td className="px-3 py-1.5 truncate max-w-[180px]" title={e.project_name}>{e.project_name}</td>
                        <td className="px-3 py-1.5 tabular-nums text-muted-foreground">{e.start_time}–{e.end_time}{e.overnight ? ' (+1d)' : ''}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums font-medium">{e.hours.toFixed(2)}</td>
                        <td className="px-3 py-1.5 text-muted-foreground truncate max-w-[200px]" title={e.notes}>{e.notes || ''}</td>
                        <td className="px-3 py-1.5 text-right whitespace-nowrap">
                          <div className="inline-flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              type="button"
                              onClick={() => setEditingEntry(e)}
                              className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
                              title="Edit this entry"
                              data-testid={`hours-edit-${e.id}`}
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeletingEntry(e)}
                              className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                              title="Delete this entry"
                              data-testid={`hours-delete-${e.id}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pay metadata form */}
          <PayMetaForm
            period={period}
            meta={meta}
            adminPass={adminPass}
            userId={userId}
            totalHours={totalHours}
            onSaved={onSaved}
          />
        </CardContent>
      )}

      <HoursEditDialog
        entry={editingEntry}
        adminPass={adminPass}
        onClose={() => setEditingEntry(null)}
        onSaved={() => { setEditingEntry(null); onEntriesChanged && onEntriesChanged(); }}
      />
      <HoursDeleteDialog
        entry={deletingEntry}
        adminPass={adminPass}
        onClose={() => setDeletingEntry(null)}
        onDeleted={() => { setDeletingEntry(null); onEntriesChanged && onEntriesChanged(); }}
      />
    </Card>
  );
}

// Inline edit dialog for a single hours entry. Hours total is recomputed
// server-side from start/end + overnight — we still show a live preview
// here so the admin can sanity-check before clicking save.
function HoursEditDialog({ entry, adminPass, onClose, onSaved }) {
  const open = !!entry;
  const [form, setForm] = useState({
    date: '', start_time: '', end_time: '', overnight: false, notes: '', project_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    if (!entry) return;
    setForm({
      date: entry.date || '',
      start_time: entry.start_time || '',
      end_time: entry.end_time || '',
      overnight: !!entry.overnight,
      notes: entry.notes || '',
      project_id: entry.project_id || '',
    });
  }, [entry]);

  // Load projects (once) so admin can move the entry to a different project.
  useEffect(() => {
    if (!open) return;
    getFurnitureProjects({ includeArchived: true })
      .then((list) => setProjects(list.filter(p => !p.is_master)))
      .catch(() => setProjects([]));
  }, [open]);

  // Local hours preview — pure UI guess, server is the source of truth.
  const previewHours = useMemo(() => {
    if (!form.start_time || !form.end_time) return null;
    const [sh, sm] = form.start_time.split(':').map(Number);
    const [eh, em] = form.end_time.split(':').map(Number);
    if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return null;
    let mins = (eh * 60 + em) - (sh * 60 + sm);
    if (form.overnight) mins += 24 * 60;
    if (mins <= 0) return null;
    return mins / 60;
  }, [form.start_time, form.end_time, form.overnight]);

  const handleSave = async () => {
    if (!entry) return;
    if (!form.start_time || !form.end_time) {
      toast.error('Start and end time are required'); return;
    }
    setSaving(true);
    try {
      await adminUpdateHoursEntry(adminPass, entry.id, form);
      toast.success('Hours entry updated');
      onSaved && onSaved();
    } catch (e) {
      toast.error(fmtErr(e, 'Failed to update'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="hours-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit hours entry</DialogTitle>
          <DialogDescription>
            {entry?.user_name} · {entry?.project_name}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Project</Label>
            <Select
              value={form.project_id}
              onValueChange={(v) => setForm((f) => ({ ...f, project_id: v }))}
            >
              <SelectTrigger data-testid="hours-edit-project"><SelectValue placeholder="Pick a project" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}{p.archived ? ' (archived)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {entry?.project_id && form.project_id !== entry.project_id && (
              <p className="text-[10px] text-amber-700 mt-1">
                Will move from &quot;{entry.project_name}&quot; to the selected project.
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs">Date</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              data-testid="hours-edit-date"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Start time</Label>
              <Input
                type="time"
                value={form.start_time}
                onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
                data-testid="hours-edit-start"
              />
            </div>
            <div>
              <Label className="text-xs">End time</Label>
              <Input
                type="time"
                value={form.end_time}
                onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))}
                data-testid="hours-edit-end"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={form.overnight}
              onChange={(e) => setForm((f) => ({ ...f, overnight: e.target.checked }))}
              data-testid="hours-edit-overnight"
            />
            Overnight (end is on the next day)
          </label>
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              data-testid="hours-edit-notes"
            />
          </div>
          <div className="text-xs text-muted-foreground">
            Preview: <span className="font-medium tabular-nums text-foreground">
              {previewHours == null ? '—' : `${previewHours.toFixed(2)}h`}
            </span>
            {entry?.hours != null && (
              <span className="ml-2 opacity-60">(was {entry.hours.toFixed(2)}h)</span>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="hours-edit-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="hours-edit-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HoursDeleteDialog({ entry, adminPass, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const open = !!entry;

  const handleDelete = async () => {
    if (!entry) return;
    setDeleting(true);
    try {
      await adminDeleteHoursEntry(adminPass, entry.id);
      toast.success('Hours entry deleted');
      onDeleted && onDeleted();
    } catch (e) {
      toast.error(fmtErr(e, 'Failed to delete'));
    } finally { setDeleting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="hours-delete-dialog">
        <DialogHeader>
          <DialogTitle>Delete hours entry?</DialogTitle>
          <DialogDescription>
            {entry && (
              <>
                <span className="block">
                  {entry.user_name} · {entry.project_name}
                </span>
                <span className="block mt-1">
                  {entry.date} · {entry.start_time}–{entry.end_time} ({entry.hours?.toFixed(2)}h)
                </span>
                <span className="block mt-2 text-destructive">This cannot be undone.</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="hours-delete-cancel">Cancel</Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="hours-delete-confirm">
            {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PayMetaForm({ period, meta, adminPass, userId, totalHours, onSaved }) {
  const [form, setForm] = useState({
    paid: !!meta?.paid,
    amount: meta?.amount != null ? String(meta.amount) : '',
    pay_date: meta?.pay_date || '',
    method: meta?.method || '',
    notes: meta?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      paid: !!meta?.paid,
      amount: meta?.amount != null ? String(meta.amount) : '',
      pay_date: meta?.pay_date || '',
      method: meta?.method || '',
      notes: meta?.notes || '',
    });
  }, [meta]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminUpsertPayPeriod(adminPass, userId, period.start, {
        period_end: period.end,
        paid: form.paid,
        amount: form.amount === '' ? null : parseFloat(form.amount),
        pay_date: form.pay_date,
        method: form.method,
        notes: form.notes,
      });
      toast.success('Pay info saved');
      onSaved && onSaved();
    } catch (e) { toast.error(fmtErr(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-md border bg-white p-4 space-y-3" data-testid={`pay-meta-form-${period.start}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm font-medium text-[#1f2933]">Payment details</p>
        <p className="text-xs text-muted-foreground">Hours total: <span className="tabular-nums font-semibold text-[#1f2933]">{totalHours.toFixed(2)} hr</span></p>
      </div>

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={form.paid}
          onChange={e => setForm(f => ({ ...f, paid: e.target.checked }))}
          className="rounded"
          data-testid={`pay-meta-paid-${period.start}`}
        />
        Marked as paid
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Amount ($)</Label>
          <Input
            type="number"
            step="0.01"
            value={form.amount}
            onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
            placeholder="0.00"
            className="h-8 text-sm mt-1"
            data-testid={`pay-meta-amount-${period.start}`}
          />
        </div>
        <div>
          <Label className="text-xs">Pay date</Label>
          <Input
            type="date"
            value={form.pay_date}
            onChange={e => setForm(f => ({ ...f, pay_date: e.target.value }))}
            className="h-8 text-sm mt-1"
            data-testid={`pay-meta-date-${period.start}`}
          />
        </div>
        <div>
          <Label className="text-xs">Method</Label>
          <Input
            value={form.method}
            onChange={e => setForm(f => ({ ...f, method: e.target.value }))}
            placeholder="Zelle, Check, Cash…"
            className="h-8 text-sm mt-1"
            data-testid={`pay-meta-method-${period.start}`}
          />
        </div>
      </div>

      <div>
        <Label className="text-xs">Notes</Label>
        <Textarea
          value={form.notes}
          onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          placeholder="Reference #, deductions, anything worth remembering…"
          rows={2}
          className="text-sm mt-1"
          data-testid={`pay-meta-notes-${period.start}`}
        />
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button size="sm" onClick={handleSave} disabled={saving} data-testid={`pay-meta-save-${period.start}`}>
          {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
          Save
        </Button>
      </div>
    </div>
  );
}

/* ── Projects list / detail ─────────────────────────────────────── */
function ProjectsList({ projects, onSelect }) {
  if (projects.length === 0) {
    return <p className="text-sm text-muted-foreground py-12 text-center">No projects with hours logged yet.</p>;
  }
  return (
    <Card data-testid="projects-list">
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Project</th>
              <th className="text-left px-4 py-2 font-medium">Staff</th>
              <th className="text-right px-4 py-2 font-medium">Sessions</th>
              <th className="text-right px-4 py-2 font-medium">Total hours</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {projects.map(p => (
              <tr
                key={p.project_id}
                className="hover:bg-emerald-50/40 cursor-pointer"
                onClick={() => onSelect(p.project_id)}
                data-testid={`project-row-${p.project_id}`}
              >
                <td className="px-4 py-2.5 font-medium">{p.project_name}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{p.by_user.length} staff member{p.by_user.length === 1 ? '' : 's'}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{p.entries}</td>
                <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{p.total_hours.toFixed(2)} hr</td>
                <td className="px-4 py-2.5"><ChevronRight className="h-4 w-4 text-muted-foreground" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function ProjectDetail({ project, entries, onBack }) {
  return (
    <div className="space-y-6">
      <button onClick={onBack} className="text-sm text-muted-foreground hover:text-[#1f2933] inline-flex items-center gap-1" data-testid="project-detail-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to projects
      </button>

      <Card>
        <CardContent className="p-5 sm:p-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-heading text-xl font-semibold text-[#1f2933]">{project.project_name}</h2>
            <p className="text-sm text-muted-foreground mt-0.5">All-staff hours logged on this project.</p>
          </div>
          <div className="grid grid-cols-2 gap-6 text-right">
            <StatBlock label="Total hours" value={`${project.total_hours.toFixed(2)} hr`} />
            <StatBlock label="Sessions" value={project.entries} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4 text-primary" />Hours by staff member</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {project.by_user.map(bu => (
              <div key={bu.user_id} className="flex items-center gap-3 text-sm" data-testid={`project-by-user-${bu.user_id}`}>
                <span className="flex-1 truncate">{bu.user_name}</span>
                <span className="text-xs text-muted-foreground tabular-nums">{bu.entries} session{bu.entries === 1 ? '' : 's'}</span>
                <span className="w-20 text-right tabular-nums font-semibold">{bu.hours.toFixed(2)} hr</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">All sessions ({entries.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Date</th>
                  <th className="text-left px-4 py-2 font-medium">Staff</th>
                  <th className="text-left px-4 py-2 font-medium">Time</th>
                  <th className="text-right px-4 py-2 font-medium">Hours</th>
                  <th className="text-left px-4 py-2 font-medium">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {entries.map(e => (
                  <tr key={e.id} className="hover:bg-muted/20">
                    <td className="px-4 py-1.5 tabular-nums">{e.date || '—'}</td>
                    <td className="px-4 py-1.5">{e.user_name}</td>
                    <td className="px-4 py-1.5 tabular-nums text-muted-foreground">{e.start_time}–{e.end_time}{e.overnight ? ' (+1d)' : ''}</td>
                    <td className="px-4 py-1.5 text-right tabular-nums font-medium">{e.hours.toFixed(2)}</td>
                    <td className="px-4 py-1.5 text-muted-foreground truncate max-w-[260px]" title={e.notes}>{e.notes || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
